import { Controller } from "@hotwired/stimulus"
import { pageIsTurboPreview } from "../packs/global/utils"
import { debounce } from "../packs/global/utils"

// Connects to data-controller="filter"
export default class extends Controller {
  static targets = ["formRow", "formElement", "textField", "searchField", "dropdownMenu", "selectedFilterText"]
  static values = {
    paramName: String,
    filterType: { type: String, default: "checkbox" }, // "checkbox", "radio", "text" oder "toggle"
    turboFrameSelector: String,
    clearParams: { type: Array, default: [] },
    clearParamsUnless: { type: String, default: "" },
  }

  connect() {
    if (pageIsTurboPreview()) return

    // TurboFrame speichern, um später beim Request nur dieses Frame zu aktualisieren
    this.turboFrame = this.turboFrameSelectorValue?.length > 0 ? document.querySelector(this.turboFrameSelectorValue) : this.element.closest("turbo-frame")
    if (this.turboFrame) {
      this.commonFilters = []
      this.turboFrameSrc = this.turboFrame?.getAttribute("src")
      this.useTurboFrameSrc = this.turboFrame.getAttribute("data-filter-use-turbo-frame-src") === "true"
    } else {
      console.error("No Turbo Frame found! This Filter needs a Turbo Frame to work properly.")
    }
  }

  subscribe(commonFilter) {
    this.commonFilters.push(commonFilter)
  }

  unsubscribe(commonFilter) {
    this.commonFilters = this.commonFilters.filter((filter) => filter !== commonFilter)
  }

  selectAll() {
    this.formElementTargets.forEach((checkbox) => (checkbox.checked = true))
  }

  deselectAll() {
    this.formElementTargets.forEach((checkbox) => (checkbox.checked = false))
  }

  clearInput() {
    this.textFieldTarget.value = null
  }

  toggleSearchField() {
    this.searchFieldTarget.classList.toggle("d-none")
    this.searchFieldTarget.focus()
  }

  setSearchFieldFocus() {
    this.searchFieldTarget.classList.remove("d-none")
    this.searchFieldTarget.focus()
  }

  updateResourceView(event) {
    const searchValue = event.target.value.toLowerCase()
    this.formRowTargets.forEach((row) => {
      const label = row.querySelector("label").textContent.toLowerCase()
      row.style.display = label.includes(searchValue) ? "block" : "none"
    })
  }

  setFocusDateField() {
    this.textFieldTarget.focus()
  }

  applyFilter() {
    this.#applyFilter()
  }

  applyFilterDebounce = debounce(() => {
    this.#applyFilter()
  }, 200)

  closeDropdownMenu() {
    if (!this.hasDropdownMenuTarget) return
    this.dropdownMenu.hide()
  }

  dropdownShown() {
    this.dispatch("fullscreen", { detail: { active: this.dropdownIsFullscreen } })
  }

  dropdownHidden() {
    this.dispatch("fullscreen", { detail: { active: false } })
  }

  // Common-Filters können mit dieser Methode den Filterinhalt setzen
  setFilterContent(content) {
    if (!content) return

    switch (this.filterTypeValue) {
      case "checkbox":
      case "radio":
        this.formElementTargets
          .filter((formElement) => formElement.value === content)
          .forEach((formElement) => {
            formElement.checked = !formElement.checked
          })
        break
    }
    this.applyFilter()
  }

  #applyFilter() {
    this.informCommonFilters()
    const selectedValues = this.selectedValues
    if (this.useTurboFrameSrc) {
      this.turboFrame?.setAttribute("src", this.updatedUrl)
    } else {
      Turbo.visit(this.updatedUrl, { frame: this.turboFrame.id, action: "advance" })
    }

    this.#updateFilterActiveState(selectedValues)
    this.#updateSelectedFilterText()
    if (this.hasDropdownMenuTarget) {
      this.closeDropdownMenu()
    }
  }

  #updateFilterActiveState(selectedValues) {
    const isActive = this.filterTypeValue === "toggle" ? selectedValues === "true" : selectedValues.length > 0

    if (isActive) {
      this.element.classList.add("filter-active")
    } else {
      this.element.classList.remove("filter-active")
    }
  }

  #updateSelectedFilterText() {
    if (!this.hasSelectedFilterTextTarget) return

    if (this.filterTypeValue !== "toggle" && this.selectedValues.length > 0) {
      this.selectedFilterTextTarget.textContent = this.selectedLabels.join(", ")
    } else {
      this.selectedFilterTextTarget.textContent = ""
    }
  }

  // Common-Filters informieren, dass sich der Filter geändert hat
  informCommonFilters() {
    this.commonFilters.forEach((commonFilter) => commonFilter.filterDidUpdate())
  }

  resetPageParams(params) {
    const possiblePageParams = ["page", "occurrences_page"]
    possiblePageParams.forEach((pageParam) => {
      if (params.has(pageParam)) {
        // Wenn ein Filter angewendet wird, springen wir immer auf die erste Seite zurück
        params.set(pageParam, "1")
      }
    })
  }

  get dropdownMenu() {
    return bootstrap.Dropdown.getOrCreateInstance(this.dropdownMenuTarget)
  }

  // Auf dem Handy wird das Dropdown per CSS (filter.scss) fullscreen dargestellt
  get dropdownIsFullscreen() {
    return getComputedStyle(this.dropdownMenuTarget).position === "fixed"
  }

  get params() {
    return new URLSearchParams(this.currentURL.search)
  }

  get updatedUrl() {
    let params = this.params
    params.set(this.paramNameValue, this.selectedValues)

    if (this.clearParamsValue.length > 0) {
      const shouldClear = !this.clearParamsUnlessValue || !this.selectedValues.includes(this.clearParamsUnlessValue)
      if (shouldClear) this.clearParamsValue.forEach((param) => params.delete(param))
    }

    this.resetPageParams(params)

    return `${this.currentURL.pathname}?${params.toString()}`
  }

  get selectedLabels() {
    switch (this.filterTypeValue) {
      case "checkbox":
      case "radio":
        return this.formElementTargets
          .filter((formElement) => formElement.checked)
          .map((formElement) => {
            return formElement.labels[0].getAttribute("data-label") || formElement.labels[0].textContent
          })
      case "text":
        return this.textFieldTarget.value
      case "toggle":
        return this.params.get(this.paramNameValue) === "true" ? "false" : "true"
      default:
        return ""
    }
  }

  get selectedValues() {
    switch (this.filterTypeValue) {
      case "checkbox":
      case "radio":
        return this.formElementTargets.filter((formElement) => formElement.checked).map((formElement) => formElement.value)
      case "text":
        return this.textFieldTarget.value
      case "toggle":
        return this.params.get(this.paramNameValue) === "true" ? "false" : "true"
      default:
        return ""
    }
  }

  get currentURL() {
    if (this.useTurboFrameSrc) {
      return new URL(this.turboFrameSrc)
    } else {
      return new URL(window.location)
    }
  }
}
